import Button from '@carvana/ds-button';
import cx from 'classnames';
import React from 'react';
import { EventedModal, ModalNames } from '|/app/modals';
import { StepName } from '|/app/services';
import currency from '|/helpers/currency';
import useNavigateToStep from '|/hooks/use-navigate-to-step';
import noevox from '|/icons/noevox.png';
import { ReviewSummaryResponse } from '|/services/purchase-api';
import styles from './trade-in-modal.module.css';
import useOrderSummary from '../use-order-summary-vm';

interface TradeInModalProps {
  summary: ReviewSummaryResponse;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const TradeInModal: React.FC<TradeInModalProps> = ({ isOpen, setIsOpen, summary }) => {
  const navigate = useNavigateToStep();
  const {
    tradeIn: {
      lienAmount = null,
      value = 0,
      year = null,
      make = null,
      model = null,
      status = null,
      trim = null,
      vin = '',
      imageUrl = ''
    } = {}
  } = summary;

  const { tradeMessage, calculatedTradeValue } = useOrderSummary(summary);

  const tradeInValue = currency(value);

  return (
    <EventedModal
      modalHeader="Trade-in breakdown"
      mobileStyle="inset"
      desktopWidth="480px"
      divLine={false}
      onClose={() => {
        setIsOpen(false);
      }}
      isOpen={isOpen}
      analyticsId={ModalNames.TradeIn}
      contentPadding={false}
    >
      <div className={styles.container} data-testid="pp_trade-in-modal">
        <div className={styles.content}>
          <div className={styles.vehicleDetails}>
            <div className={styles.detailsText}>
              <h4>{[year, make, model].join(' ')}</h4>
              <p>{trim}</p>
              {vin && <p>{vin}</p>}
            </div>
            <div className={styles.vehicleImage}>
              <img src={imageUrl || noevox} alt="Trade-in vehicle" />
            </div>
          </div>
          <div className={styles.pricingDetails}>
            <div className={styles.equation}>
              <div className={styles.itemRow}>
                <p>Your Carvana offer</p>
                <p>{tradeInValue}</p>
              </div>
              {lienAmount !== null && lienAmount > 0 && (
                <>
                  <div className={cx(styles.itemRow, styles.lesserRow)}>
                    <p>Trade-in loan balance</p>
                    <p>-{currency(lienAmount ?? 0)}</p>
                  </div>

                  <div className={styles.itemRow}>
                    <p>{calculatedTradeValue < 0 ? 'Amount you owe' : 'Trade-in value'}</p>
                    <p>{currency(calculatedTradeValue)}</p>
                  </div>
                </>
              )}
            </div>
            <div>
              <div
                className={styles.messageHtml}
                dangerouslySetInnerHTML={{ __html: tradeMessage }}
              />
            </div>
          </div>
        </div>
        <div className={styles.actions}>
          <Button
            variant="tertiary"
            data-testid="edit-trade-in-button"
            data-analytics-id="pp_trade_in_modal_edit_button"
            onClick={() => {
              navigate(StepName.TradeIn);
            }}
          >
            Edit
          </Button>
          <Button
            data-testid="close-trade-in-button"
            data-analytics-id="pp_trade_in_modal_close_button"
            onClick={() => {
              setIsOpen(false);
            }}
          >
            Close
          </Button>
        </div>
      </div>
    </EventedModal>
  );
};

export default TradeInModal;
